import { withCloudinaryTransform } from "./cloudinary";
import { openDialog } from "./dialog";
import { themeOverridesCss } from "./theme";
import triggerStyles from "./trigger.css";
import type { ConfigResponse, EmbedOptions } from "./types";

// Entry point. Runs once when the embed script loads.
//
// Reads config from the current <script> tag's data-* attrs:
//   data-agent="acmecoffee.com"        (required)
//   data-api="https://chatporch.example" (optional; defaults to script's origin)
//   data-mount="#some-selector"        (optional; inline mode instead of floating)
//   data-no-button                     (optional; suppress our trigger button —
//                                       site drives opening via its own triggers)
//
// Flow: fetch /v1/config → if agent is enabled + not rate-limited, inject a
// trigger button → on click, lazy-init the dialog module.
//
// Customer-supplied triggers (work regardless of data-no-button):
//   - any element with a `data-chatporch-open` attribute opens the dialog
//   - window.chatporch.open() does the same programmatically

declare global {
  interface Window {
    chatporch?: { open: () => void };
  }
}

// Set by start() once config resolves AND the agent is allowed. Custom
// triggers bound at bootstrap funnel through requestOpen so a click that
// lands before the config fetch finishes is queued, and clicks while the
// agent is disabled/rate-limited drop silently — same behavior as the
// floating button, which is hidden in that state.
let openChat: (() => void) | null = null;
let pendingOpen = false;

function requestOpen() {
  if (openChat) openChat();
  else pendingOpen = true;
}

(function bootstrap() {
  const script = (document.currentScript ??
    findScript()) as HTMLScriptElement | null;
  if (!script) {
    console.warn("[chatporch] could not locate embed script tag");
    return;
  }

  const agent = script.dataset.agent;
  if (!agent) {
    console.warn("[chatporch] missing data-agent on script tag");
    return;
  }

  const apiBase =
    script.dataset.api ?? new URL(script.src, location.href).origin;
  const mountSel = script.dataset.mount ?? null;
  const mount = mountSel ? document.querySelector<HTMLElement>(mountSel) : null;
  const noButton = script.dataset.noButton != null;

  const opts: EmbedOptions = { agent, apiBase, mount, noButton };

  // One delegated listener bound once at init — catches elements added after
  // load (SPA re-renders) with no polling or MutationObserver.
  document.addEventListener("click", (e) => {
    if (!(e.target instanceof Element)) return;
    if (!e.target.closest("[data-chatporch-open]")) return;
    // The element exists to open chat — stop <a href="#"> from scroll-jumping.
    e.preventDefault();
    requestOpen();
  });
  window.chatporch = { open: requestOpen };

  // Defer until DOM is interactive so query selectors can find the mount.
  ready(() => void start(opts));
})();

function findScript(): HTMLScriptElement | null {
  // Fallback when document.currentScript is null (async/defer in some edge cases).
  const scripts = document.querySelectorAll<HTMLScriptElement>(
    "script[data-agent][src*='chatporch']",
  );
  return scripts[scripts.length - 1] ?? null;
}

function ready(fn: () => void) {
  if (document.readyState !== "loading") fn();
  else document.addEventListener("DOMContentLoaded", fn, { once: true });
}

async function start(opts: EmbedOptions) {
  let payload: ConfigResponse;
  try {
    const res = await fetch(
      `${opts.apiBase}/v1/config?agent=${encodeURIComponent(opts.agent)}`,
      { credentials: "omit" },
    );
    if (!res.ok) {
      console.warn(`[chatporch] config fetch failed: ${res.status}`);
      return;
    }
    payload = (await res.json()) as ConfigResponse;
  } catch (err) {
    console.warn("[chatporch] config fetch error", err);
    return;
  }

  if (!payload.status.allowed) return; // hide the button; custom triggers no-op

  const { config } = payload;
  openChat = () => openDialog(opts, config);
  if (pendingOpen) {
    pendingOpen = false;
    openChat();
  }

  if (!opts.noButton) mountTrigger(opts, payload);
}

// ⚠️ SYNC: dashboard/src/AppearancePreview.svelte mirrors the button DOM
// built below (text branch: svg sparkles + label span; image branch: <img>
// alone). If you change the SVG path, swap the label fallback, or restructure
// the children, update the preview to match.
function mountTrigger(opts: EmbedOptions, { config }: ConfigResponse) {
  const host = document.createElement("div");
  // `all: initial` would also reset font-family to the UA default (Times New
  // Roman). Re-enable inheritance for font so the button picks up the host
  // page's typography — anything inside the shadow root can `font: inherit`.
  host.style.cssText = "all: initial; font: inherit;";
  const shadow = host.attachShadow({ mode: "closed" });

  const style = document.createElement("style");
  // Theme overrides come AFTER the main stylesheet so DB-configured colors
  // win over the :host fallbacks declared in trigger.css.
  style.textContent = triggerStyles + themeOverridesCss(config.themeOverrides);
  shadow.appendChild(style);

  const label = config.triggerLabel || "Ask AI";
  const btn = document.createElement("button");
  btn.type = "button";
  // In image mode the dashboard hides `triggerLabel`, so we don't surface a
  // stale value to SR users — fall back to a hardcoded label.
  btn.setAttribute("aria-label", config.triggerImageUrl ? "Ask AI" : label);
  const classes = [opts.mount ? "trigger-inline" : "trigger-floating"];
  if (config.triggerImageUrl) classes.push("trigger-image");
  btn.className = classes.join(" ");
  if (config.triggerImageUrl) {
    // Image is the entire visual; decorative inside a labeled button.
    // c_fill + g_auto smart-crops to a square at 128×128 (2x retina for the
    // 64px clipped-circle display). Square delivery means the circle in CSS
    // is a true circle even when the upload is a wide logo; g_auto picks the
    // most salient region so we don't lop off the subject.
    const img = document.createElement("img");
    img.src = withCloudinaryTransform(
      config.triggerImageUrl,
      "c_fill,g_auto,w_128,h_128,f_auto,q_auto",
    );
    img.alt = "";
    btn.appendChild(img);
  } else {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("aria-hidden", "true");
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute(
      "d",
      "M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z",
    );
    path.setAttribute("fill", "currentColor");
    svg.appendChild(path);
    const span = document.createElement("span");
    span.textContent = label;
    btn.appendChild(svg);
    btn.appendChild(span);
  }
  (opts.mount ?? document.body).appendChild(host);
  shadow.appendChild(btn);

  btn.addEventListener("click", () => {
    openDialog(opts, config);
  });

  // Floating text pills mirror the host page's per-section theming the same
  // way a Scalebloom site header does: when a `.dark-theme` section scrolls
  // behind the pill, its palette tokens get copied onto the shadow host so the
  // pill re-inverts and stays legible. Skipped for inline triggers (they sit
  // inside a section and inherit its tokens already) and image triggers (no
  // pill to recolor). No-ops on sites without `.dark-theme` sections.
  if (!opts.mount && !config.triggerImageUrl) syncTriggerToSections(host, btn);
}

// Keep the floating pill legible over per-section themes. A fixed-position
// button is a child of <body>, not of the section scrolled behind it, so it
// never picks up that section's overridden palette on its own — the host page
// header has the exact same problem and solves it with an IntersectionObserver.
// We do the same, then copy the active section's tokens onto the shadow host;
// CSS custom properties are the one thing that inherits across the shadow
// boundary, so the closed shadow root is no obstacle.
function syncTriggerToSections(host: HTMLElement, btn: HTMLElement) {
  const sections = document.querySelectorAll<HTMLElement>(".dark-theme");
  if (!sections.length) return; // convention not in use → nothing to mirror

  // The only tokens the pill's background/color resolve from (see trigger.css
  // :host). Copy just these; everything else stays inherited from the page.
  const TOKENS = ["--clr-base", "--clr-contrast"] as const;
  const active = new Set<Element>();

  const paint = () => {
    const section = active.values().next().value as HTMLElement | undefined;
    if (section) {
      const cs = getComputedStyle(section);
      for (const token of TOKENS) {
        const value = cs.getPropertyValue(token).trim();
        if (value) host.style.setProperty(token, value);
      }
    } else {
      for (const token of TOKENS) host.style.removeProperty(token);
    }
  };

  let observer: IntersectionObserver | undefined;
  const rebuild = () => {
    observer?.disconnect();
    active.clear();
    // Shrink the observer root to a viewport band the height of the button at
    // its fixed position, so a section counts as "behind" the pill only while
    // it actually overlaps that band — the header's top-strip trick, flipped
    // to the bottom of the viewport.
    const rect = btn.getBoundingClientRect();
    const top = Math.max(0, Math.round(rect.top));
    const bottom = Math.max(0, Math.round(window.innerHeight - rect.bottom));
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) active.add(entry.target);
          else active.delete(entry.target);
        }
        paint();
      },
      { rootMargin: `-${top}px 0px -${bottom}px 0px` },
    );
    sections.forEach((section) => observer!.observe(section));
  };

  rebuild();
  // The pill's fixed position/size shifts with viewport width (its offsets are
  // clamp()ed) and with late-loading host fonts — re-measure the band on both.
  window.addEventListener("resize", rebuild, { passive: true });
  new ResizeObserver(rebuild).observe(btn);
}
